import { useState, useEffect, useCallback, useContext } from 'react';
import styles from './ParkingProjectPage.module.css';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import ParkingFilter from '../../components/FiltersComponents/SmallFiltersComponents/ParkingFilter/ParkingFilter';
import { ParkingCardData } from '../../components/CardsComponents/types';
import Features, { FeaturesData } from '../../components/Features/Features';
import { HeaderType } from '../../components/Header/types/Header';
import { FooterType } from '../../components/Footer/types/Footer';
import { Helmet } from 'react-helmet';
import ParkingCard from '../../components/CardsComponents/ParkingCard/ParkingCard';
import NotFoundBlock from '../../components/NotFoundBlock/NotFoundBlock';
import Loader from '../../components/Loader/Loader';
import { LoaderContext } from '../../components/Loader/LoaderContext';
import CardsLoader from '../../assets/images/CardsLoader.gif'
import api from '../../utils/api';
import { transformParkings, transformParkingFeatures } from '../../utils/transformers';

const INITIAL_LOAD_COUNT = 6;
const LOAD_MORE_COUNT = 6;

export default function ParkingProjectPage() {
  const [cardData, setCardData] = useState<ParkingCardData[]>([]);
  const [displayedCards, setDisplayedCards] = useState<ParkingCardData[]>([]);
  const [featuresData, setFeaturesData] = useState<FeaturesData[]>([]);
  const [filteredData, setFilteredData] = useState<ParkingCardData[]>([]);
  const { isInitialLoad, setIsInitialLoad } = useContext(LoaderContext);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 720);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [featuresResponse, parkingResponse] = await Promise.all([
          api.parking.getFeatures(),
          api.parking.getAll(),
        ]);

        const featuresData = transformParkingFeatures(featuresResponse);
        const parkingData = transformParkings(parkingResponse);
  
        const parkings = parkingData.filter((parking: any) => parking.available === true);
        parkings.sort((a: any, b: any) => (b.popular ? 1 : 0) - (a.popular ? 1 : 0));
  
        const prices = parkings
          .map((parking: any) => {
            if (parking.price == null) return null;
            const price = typeof parking.price === 'string' ? parseFloat(parking.price.replace(/ /g, '')) : parking.price;
            const discount = (parking.discountProperty ?? parking.discount_property ?? 0) / 100;
            return price * (1 - discount);
          })
          .filter((price: number | null): price is number => price !== null);
  
        const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
        const formattedPrice = minPrice.toLocaleString('ru-RU');
  
        if (minPrice > 0) {
          featuresData.push({
            id: featuresData.length + 1,
            text: `От ${formattedPrice} ₽`,
            image: undefined,
          });
        }
  
        setCardData(parkings);
        setFilteredData(parkings);
        setDisplayedCards(parkings.slice(0, INITIAL_LOAD_COUNT));
        setFeaturesData(featuresData);
      } catch (error) {
        console.error('Ошибка при загрузке данных:', error);
      } finally {
        setIsInitialLoad(false);
      }
    };
  
    fetchData();
  }, [setIsInitialLoad]);

  const loadMoreFilteredCards = useCallback(() => {
    setIsLoadingMore(true);
    setTimeout(() => {
      setDisplayedCards((prevCards) => {
        const nextCards = filteredData.slice(prevCards.length, prevCards.length + LOAD_MORE_COUNT);
        setIsLoadingMore(false);
        return [...prevCards, ...nextCards];
      });
    }, 500);
  }, [filteredData]);

  const handleScroll = useCallback(() => {
    const scrollY = window.scrollY;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
  
    const hasMoreCards = displayedCards.length < filteredData.length;
  
    const offset = isMobile ? 1400 : 300;
  
    if (scrollY + windowHeight >= documentHeight - offset && !isLoadingMore && hasMoreCards) {
      loadMoreFilteredCards();
    }
  }, [loadMoreFilteredCards, isLoadingMore, displayedCards.length, filteredData.length, isMobile]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const applySmallFilter = useCallback(
    (filteredData: ParkingCardData[]) => {
      setFilteredData(filteredData);
      setDisplayedCards(filteredData.slice(0, INITIAL_LOAD_COUNT));
    },
    []
  );

  return (
    <>
      <Helmet>
        <title>Машино-места | Восток Строй</title>
        <meta name="description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta name="keywords" content="недвижимость, девелопер, Каспийск, новостройки, квартиры" />
        <meta name="author" content="Восток Строй" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://vostokstroy.com/parking/status" />

        <meta property="og:title" content="Машино-места | Восток Строй" />
        <meta property="og:description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta property="og:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" />
        <meta property="og:url" content="https://vostokstroy.com/parking/status" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Восток Строй" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Машино-места | Восток Строй" />
        <meta name="twitter:description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta name="twitter:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" />

        <link rel="icon" type="image/svg+xml" href="/favicons/status-fav-32x32.svg" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicons/status-fav-16x16.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicons/status-fav-32x32.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/favicons/status-apple-touch-icon-180x180.png" />
        <link rel="icon" type="image/png" sizes="180x180" href="/favicons/status-fav-180x180.png" />
      </Helmet>

      {isInitialLoad ? (
        <Loader />
      ) : (
        <>
          <Header theme={HeaderType.Light} />
          <div className={styles.content}>
            <div className={styles.wrapper}>
              <div className={styles.cars_content}>
                <div className={styles.page_title}>
                  <h1>
                    Машино-места
                    <br />в «Статусе»
                  </h1>
                </div>
                <Features features={featuresData} hideIcons={true} />
                <ParkingFilter
                  onApplyFilter={applySmallFilter}
                  cardData={cardData}
                />
                <div className={styles.list_wrapper}>
                  {filteredData.length === 0 ? (
                    <NotFoundBlock type="filter" />
                  ) : (
                    <div className={styles.list}>
                      {displayedCards.map((data, index) => (
                        <div 
                          key={data.id} 
                          className={styles.parking_card} 
                          style={{ 
                            animationDelay: `${index * (isMobile ? 0.02 : 0.03)}s` 
                          }}
                        >
                          <ParkingCard parkingData={data} isDiscounted={!!data.discountProperty} />
                        </div>
                      ))}
                      {isLoadingMore && (
                        <div className={styles.loader_container}>
                          <div className={styles.loader}>
                            <img src={CardsLoader} alt="Loading..." />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <Footer theme={FooterType.Light} />
        </>
      )}
    </>
  );
}